import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CheckIcon } from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { SettingsButton } from '~/components/AssetModalV2/Content/Footer/components/SettingsButton';
import { useImageContentControlsContext } from '~/components/AssetModalV2/Content/ImageContentControlsContextProvider';
import { centralizedClipExtSelector } from '~/store/centralizedClip/selectors';
import { isTransparentPreviewExtension } from '~/utils/getTransparentPreviewSource';

export const ImageSettingsButton = memo(() => {
  const clipExt = useSelector(centralizedClipExtSelector);
  const { showOriginal, setShowOriginal, showSolidBackground, setShowSolidBackground } =
    useImageContentControlsContext();
  // AI/SVG already show transparency by default, so the item offers the solid background instead.
  const isTransparentByDefault = isTransparentPreviewExtension(clipExt);

  const handleSelectShowOriginal = useCallback(() => {
    setShowOriginal(!showOriginal);
  }, [showOriginal, setShowOriginal]);

  const handleSelectShowSolidBackground = useCallback(() => {
    setShowSolidBackground(!showSolidBackground);
  }, [showSolidBackground, setShowSolidBackground]);

  const options: DropdownMenuOption[] = useMemo(
    () => [
      isTransparentByDefault
        ? {
            label: 'Show with background',
            onSelect: handleSelectShowSolidBackground,
            id: 'show-with-background',
            suffix: showSolidBackground ? <CheckIcon size="sm" className="ads:text-icon-primary text-blue-9" /> : null,
            type: 'item',
            truncate: false,
          }
        : {
            label: 'Show image transparency',
            onSelect: handleSelectShowOriginal,
            id: 'show-original',
            suffix: showOriginal ? <CheckIcon size="sm" className="ads:text-icon-primary text-blue-9" /> : null,
            type: 'item',
            truncate: false,
          },
    ],
    [
      handleSelectShowOriginal,
      handleSelectShowSolidBackground,
      isTransparentByDefault,
      showOriginal,
      showSolidBackground,
    ],
  );

  return <SettingsButton options={options} data-testid="IMAGE_SETTINGS_BUTTON" />;
});

ImageSettingsButton.displayName = 'ImageSettingsButton';
